import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { checkBrowserPrint, printZplChunked, PrintAborted, ZEBRA_FLUSH_HINT } from "@/lib/zebra";
import { getPrintChunkSize } from "@/lib/printSettings";
import { usePrintConfirm } from "@/lib/usePrintConfirm";
import { usePrintJob } from "@/lib/usePrintJob";
import { sizeToMedia } from "@/lib/tagMedia";

const sizeWord = (s) => s.replace("x", "×");

// PRINT WHAT THE PRINT LABELS SHEET PREVIEWED, from any screen — the same files, the printed stamp only for what
// really went out, and a 4×3 holder + 3×1 slots split around one roll change. Returns { printSheet, printDialogs }.
export function useSheetPrinter(onPrinted) {
  const { printConfirmDialog, confirmAndRun } = usePrintConfirm();
  const printJob = usePrintJob();
  const [roll, setRoll] = useState(null);

  const run = (labels, title, media, onAllSent) => confirmAndRun({ title, labelCount: labels.length, copies: 1, noun: "labels", mediaType: media }, async () => {
    if (!(await checkBrowserPrint()).ok) { toast.error("Zebra Browser Print not reachable — check the printer connection."); return; }
    const tId = toast.loading(`Printing ${labels.length} labels…`);
    const signal = printJob.start(title, labels.length);
    let sent = labels.length;
    try {
      await printZplChunked(labels.map((l) => l.zpl), { chunkSize: getPrintChunkSize(), signal, mediaType: media,
        onProgress: (done, tot) => { printJob.progress(done, tot); toast.loading(`Printing ${done}/${tot} labels…`, { id: tId }); } });
      toast.success(`Printed ${labels.length} labels.`, { id: tId });
    } catch (e) {
      sent = e.done ?? 0;
      toast.error(e instanceof PrintAborted || e.aborted
        ? `Stopped after ${sent} of ${labels.length} — the ones that printed are recorded. Print labels again for the rest. ${ZEBRA_FLUSH_HINT}`
        : `Printing failed after ${sent} of ${labels.length} — check the Zebra connection. (${e.message})`, { id: tId, duration: 12000 });
    } finally { printJob.end(); }
    const ids = [...new Set(labels.slice(0, sent).map((l) => l.id).filter(Boolean))];
    if (ids.length) await api.post("/locations/label-printed/confirm", { location_ids: ids }).catch(() => {});
    if (sent === labels.length && onAllSent) onAllSent();
    onPrinted?.();
  });

  const printSheet = (data, title) => {
    const [first, second] = data.sizes;
    const pick = (size) => data.items.filter((i) => i.size === size).map(({ zpl, id, code }) => ({ zpl, id, code }));
    run(pick(first), second ? `${title} · the holder` : title, sizeToMedia(first),
      second ? () => setRoll({ size: second, title: data.place_code, labels: pick(second) }) : null);
  };

  const printDialogs = (
    <>
      {printConfirmDialog}
      {roll && (
        <div data-testid="roll-change-card" className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 sm:max-w-sm rounded-xl border-2 border-[#E0781A] bg-white p-4 shadow-xl">
          <div className="text-sm font-bold">Now load the {sizeWord(roll.size)} roll</div>
          <div className="text-[12px] text-slate-600 mt-1">Then print the {roll.labels.length} labels for what's inside {roll.title}, in order.</div>
          <div className="flex gap-2 mt-3">
            <Button data-testid="roll-change-print" className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white"
              onClick={() => { const r = roll; setRoll(null); run(r.labels, `${r.title} · inside`, sizeToMedia(r.size)); }}>
              Print {roll.labels.length} labels
            </Button>
            <Button data-testid="roll-change-later" variant="outline" className="rounded-lg" onClick={() => setRoll(null)}>Not now</Button>
          </div>
        </div>
      )}
    </>
  );
  return { printSheet, printDialogs };
}
